/**
 * Coque de l'application — fidèle à la v1 : barre noire (logo/opérateur,
 * MON PLAYTIME · PLANNINGS · PRESTATIONS · COMPTEURS · PARAMÈTRES, nom de
 * l'utilisateur avec menu), sous-onglets par section, écran de démarrage
 * selon les préférences. Améliorations : responsive, clavier, bannière GodView.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Administration } from './admin/administration';
import { SuperAdministration } from './admin/superadmin';
import { api, type EcranDemarrage, type Notification, type Profil, type Referentiels, type Theme } from './api';
import { appliquerTheme, naviguer, useChargement, useRoute } from './commun';
import { Connexion, OuvertureGodView, Reinitialisation } from './ecrans/acces';
import { Compteurs, MonPlaytime, TableauDeBord } from './ecrans/consultation';
import { EcranMois } from './ecrans/mois';
import { Aide } from './ecrans/aide';
import { Tickets } from './ecrans/tickets';

const SECTIONS: { cle: string; libelle: string; sousOnglets: [string, string][]; admin?: boolean; superAdmin?: boolean }[] = [
  { cle: 'operateurs', libelle: 'OPÉRATEURS', superAdmin: true, sousOnglets: [] },
  { cle: 'tableau-de-bord', libelle: 'MON PLAYTIME', sousOnglets: [] },
  { cle: 'planning', libelle: 'PLANNINGS', sousOnglets: [['planning', 'Mon planning'], ['planning-equipe', 'Le planning de mon équipe'], ['planning-outils', 'Outils']] },
  { cle: 'prestations', libelle: 'PRESTATIONS', sousOnglets: [['prestations', 'Mes prestations'], ['prestations-equipe', 'Les prestations de mon équipe'], ['prestations-outils', 'Outils']] },
  { cle: 'compteurs', libelle: 'COMPTEURS', sousOnglets: [['compteurs', 'Mes compteurs'], ['compteurs-equipe', 'Les compteurs de mon équipe'], ['compteurs-outils', 'Outils']] },
  { cle: 'aide', libelle: 'AIDE', sousOnglets: [['aide', 'Documentation'], ['tickets', 'Tickets']] },
  { cle: 'parametres', libelle: 'PARAMÈTRES', admin: true, sousOnglets: [
    ['parametres', 'Organigramme et utilisateurs'], ['parametres-calendrier', 'Calendrier et programmation'], ['parametres-fonctions', 'Fonctions et groupes'],
    ['parametres-constantes', 'Constantes'], ['parametres-techniques', 'Paramètres techniques'], ['parametres-gdpr', 'GDPR'],
  ] },
];
const ECRANS: [EcranDemarrage, string][] = [
  ['tableau-de-bord', 'Tableau de bord PlayTime'], ['planning', 'Mon planning'], ['planning-equipe', 'Le planning de mon équipe'], ['prestations', 'Mes prestations'],
  ['prestations-equipe', 'Les prestations de mon équipe'], ['compteurs', 'Mes compteurs'], ['compteurs-equipe', 'Les compteurs de mon équipe'],
];
const VUES: [('JOUR' | 'SEMAINE'), ('24H' | '7-19' | '5-22'), string][] = [
  ['JOUR', '24H', 'Par jour, sur 24h'], ['JOUR', '7-19', 'Par jour, de 7h à 19h'], ['JOUR', '5-22', 'Par jour, de 5h à 22h'],
  ['SEMAINE', '24H', 'Par semaine, sur 24h'], ['SEMAINE', '7-19', 'Par semaine, de 7h à 19h'], ['SEMAINE', '5-22', 'Par semaine, de 5h à 22h'],
];

function sectionDe(chemin: string): (typeof SECTIONS)[number] {
  return SECTIONS.find((s) => s.cle === chemin || s.sousOnglets.some(([c]) => c === chemin)) ?? SECTIONS[0]!;
}

export function App(): JSX.Element {
  const route = useRoute();
  const [profil, setProfil] = useState<Profil | null | 'inconnu'>('inconnu');
  const [menuOuvert, setMenuOuvert] = useState(false);
  // Thème actif (session, préférence) : son nom et son logo remplacent ceux de l'opérateur de la session dans la barre.
  const [themeActif, setThemeActif] = useState<Theme | null>(null);
  // Lot 8 : thème de l'opérateur de la session (vide = Théâtre National) ; déclaré avant tout retour anticipé.
  useEffect(() => {
    if (!profil || profil === 'inconnu') return;
    // Priorité : le thème choisi sur l'écran de connexion pour cette session (onglet du navigateur), puis le thème de
    // Mes préférences, puis celui de l'opérateur de la session. Un thème introuvable retombe sur l'opérateur.
    let session: string | null = null;
    try { session = window.sessionStorage.getItem('playtime.theme.session'); } catch { /* stockage indisponible */ }
    const choisi = session ?? profil.preferences.theme ?? null;
    if (choisi && choisi !== profil.operateur.code) {
      api.theme(choisi).then((t) => { appliquerTheme(t.style); setThemeActif(t); }).catch(() => { appliquerTheme(profil.operateur.style); setThemeActif(null); });
    } else { appliquerTheme(profil.operateur.style); setThemeActif(null); }
  }, [profil]);
  const [demarre, setDemarre] = useState(false);
  const chargerSession = useCallback(() => { api.moi().then(setProfil).catch(() => setProfil(null)); }, []);
  useEffect(chargerSession, [chargerSession]);
  const referentiels = useChargement<Referentiels | null>(() => (profil && profil !== 'inconnu' ? api.referentiels() : Promise.resolve(null)), [profil === 'inconnu' ? 0 : profil?.id]);

  // Écran de démarrage (préférence v1) à l'entrée dans la session.
  useEffect(() => {
    if (profil && profil !== 'inconnu' && !demarre) {
      setDemarre(true);
      if (!window.location.hash || window.location.hash === '#/' || window.location.hash === '#/connexion') naviguer(profil.preferences.menuDemarrage);
    }
  }, [profil, demarre]);

  if (route.chemin === 'reinitialisation' && route.params.get('token')) return <Reinitialisation token={route.params.get('token')!} />;
  if (route.chemin === 'godview' && route.params.get('guid')) return <OuvertureGodView guid={route.params.get('guid')!} surSession={chargerSession} />;
  if (profil === 'inconnu') return <div />;
  if (profil === null) return <Connexion surConnexion={() => { setDemarre(false); chargerSession(); }} />;

  // Lot 2 : un GodView ouvert par un superadministrateur garde l'administration (DroitsGuard côté serveur).
  const admin = profil.droits === 9 && (!profil.impersonePar || profil.impersonantSuperAdmin);
  const superAdmin = profil.superAdmin && !profil.impersonePar;
  // FramePlannings.pas L91-110, FramePrestations.pas L88-107, FrameCompteurs.pas L90-97 : « … de mon équipe » et
  // « Outils » ne sont visibles que si Droits > 0 (chef de service, lecteur global, administrateur) ; le travailleur
  // (0) ne voit que « Mon … ». (AIDE garde ses deux sous-onglets : documentation et tickets, nouveaux en v2.)
  const sections = SECTIONS.filter((s) => (!s.admin || admin) && (!s.superAdmin || superAdmin)).map((s) => (
    profil.droits > 0 || s.cle === 'aide' || s.cle === 'parametres' ? s : { ...s, sousOnglets: s.sousOnglets.filter(([cle]) => !cle.endsWith('-equipe') && !cle.endsWith('-outils')) }
  ));
  // La section courante est prise dans la liste FILTRÉE : ses sous-onglets sont ceux autorisés pour ce compte.
  const section = sections.find((s) => s.cle === sectionDe(route.chemin).cle) ?? sectionDe(route.chemin);

  let contenu: JSX.Element;
  // Travailleur (droits 0) : les écrans « équipe » et « Outils » lui sont fermés même par l'adresse (règle v1 des conteneurs).
  const cheminReserve = profil.droits === 0 && /-(equipe|outils)$/.test(route.chemin);
  switch (cheminReserve ? 'acces-reserve' : route.chemin) {
    case 'acces-reserve': contenu = <p className="erreur">Accès réservé aux responsables (chef de service, lecteur global, administrateur).</p>; break;
    case 'planning': case 'planning-equipe':
      contenu = <EcranMois profil={profil} cible="planning" equipe={route.chemin.endsWith('equipe')} params={route.params} referentiels={referentiels.donnees} />; break;
    case 'prestations': case 'prestations-equipe':
      contenu = <EcranMois profil={profil} cible="prestations" equipe={route.chemin.endsWith('equipe')} params={route.params} referentiels={referentiels.donnees} />; break;
    case 'compteurs': case 'compteurs-equipe':
      contenu = <Compteurs profil={profil} equipe={route.chemin.endsWith('equipe')} params={route.params} referentiels={referentiels.donnees} />; break;
    case 'planning-outils': case 'prestations-outils': case 'compteurs-outils':
      contenu = <Outils section={route.chemin} admin={admin} />; break;
    case 'profil': contenu = <MonPlaytime params={route.params} />; break;
    case 'operateurs': contenu = superAdmin ? <SuperAdministration /> : <p className="erreur">Accès réservé au superadministrateur.</p>; break;
    case 'aide': contenu = <Aide params={route.params} />; break;
    case 'tickets': contenu = <Tickets profil={profil} params={route.params} />; break;
    case 'parametres': case 'parametres-calendrier': case 'parametres-fonctions': case 'parametres-constantes': case 'parametres-techniques': case 'parametres-gdpr':
      contenu = admin ? <Administration onglet={route.chemin} params={route.params} referentiels={referentiels.donnees} rechargerReferentiels={referentiels.recharger} /> : <p className="erreur">Accès réservé à l'administrateur.</p>; break;
    default: contenu = superAdmin && route.chemin !== 'tableau-de-bord' ? <SuperAdministration /> : <TableauDeBord />;
  }

  return (
    <div className={`coque-app${menuOuvert ? ' menu-ouvert' : ''}`}>
      {profil.impersonePar && (
        <div className="banniere-godview" role="status">Connecté en tant que <strong>{profil.prenom} {profil.nom}</strong> ({profil.operateur.nom}) — {profil.impersonantSuperAdmin ? 'ouvert par le superadministrateur, administration permise' : 'administration désactivée'}, session journalisée. <button className="discret" onClick={() => void api.logout().then(() => { effacerAdresse(); setProfil(null); })}>Quitter</button></div>
      )}
      <header className="barre-principale">
        <div className="marque-operateur">{(() => { const logo = themeActif ? themeActif.logo : (profil.operateur.style?.logoUrl ?? null); const sigle = themeActif ? themeActif.style.sigle : profil.operateur.style?.sigle; const nom = themeActif ? themeActif.nom : profil.operateur.nom; return <>{logo ? <img className="logo-image" src={logo} alt="" /> : <span className="logo-operateur">{sigle || 'P.'}</span>}<span className="nom-operateur"><strong>{nom || 'Playtime'}</strong><small>Gestion des plannings et des prestations</small></span></>; })()}</div>
        {/* Lot 4 : sur smartphone, le menu principal se replie derrière ce bouton. */}
        <button className="bouton-menu" aria-label="Menu" aria-expanded={menuOuvert} onClick={() => setMenuOuvert(!menuOuvert)}>☰</button>
        <nav className="menu-principal" aria-label="Menu principal">
          {sections.map((s) => <button key={s.cle} aria-current={section.cle === s.cle} onClick={() => { setMenuOuvert(false); naviguer(sousOngletPrefere(s.cle, profil.preferences.menuDemarrage)); }}>{s.libelle}</button>)}
        </nav>
        <Cloche />
        <MenuNom profil={profil} admin={admin} surDeconnexion={() => void api.logout().then(() => { effacerAdresse(); setProfil(null); })} surPreferences={chargerSession} />
      </header>
      {section.sousOnglets.length > 0 && (
        <nav className="sous-onglets" aria-label="Sous-menu">
          {section.sousOnglets.map(([cle, lib]) => <button key={cle} aria-current={route.chemin === cle} onClick={() => naviguer(cle)}>{lib}</button>)}
        </nav>
      )}
      <main className="plateau">{contenu}</main>
    </div>
  );
}

/**
 * FramePlannings.pas L46-70, FramePrestations.pas L46-71, FrameCompteurs.pas L64-66 : à chaque ouverture du menu,
 * le sous-onglet ouvert est celui de la préférence de démarrage quand elle porte sur ce menu (sinon « Mon … »).
 * La v1 testait « menu = plannings » dans les trois conteneurs (copier-coller) ; la v2 applique la préférence de
 * chaque menu (décision du 20/09/2026, §44).
 */
/** À la déconnexion, l'adresse ne garde ni route ni paramètres (contrat, mois, op…) : la personne suivante arrive
 *  sur SON écran de démarrage, pas sur la page de la précédente. Le thème de session est oublié aussi. */
function effacerAdresse(): void {
  try { window.sessionStorage.removeItem('playtime.theme.session'); } catch { /* ignoré */ }
  window.history.replaceState(null, '', window.location.pathname);
}

function sousOngletPrefere(section: string, prefere: string): string {
  return prefere === `${section}-equipe` ? prefere : section;
}

/** Cloche des notifications en ligne (adaptation FEAS n° 1) : compteur de non lues, liste, clic → lien + marquage lu. Rafraîchie toutes les 60 s. */
function Cloche(): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const [donnees, setDonnees] = useState<{ nonLues: number; notifications: Notification[] } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const charger = useCallback(() => { void api.notifications().then(setDonnees).catch(() => undefined); }, []);
  useEffect(() => { charger(); const t = setInterval(charger, 60_000); return () => clearInterval(t); }, [charger]);
  useEffect(() => {
    if (!ouvert) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOuvert(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, [ouvert]);
  function ouvrir(n: Notification): void {
    setOuvert(false);
    if (!n.dateLecture) void api.notificationLue(n.id).then(charger);
    if (n.lien) { const [chemin, q] = n.lien.split('?'); window.location.hash = `#/${chemin}${q ? `?${q}` : ''}`; }
  }
  const nonLues = donnees?.nonLues ?? 0;
  return (
    <div className="cloche" ref={ref}>
      <button className="bouton-cloche" aria-label={`Notifications, ${nonLues} non lue(s)`} aria-haspopup="menu" aria-expanded={ouvert} onClick={() => setOuvert(!ouvert)}>
        🔔{nonLues > 0 && <span className="badge-cloche">{nonLues > 99 ? '99+' : nonLues}</span>}
      </button>
      {ouvert && (
        <div className="panneau-cloche" role="menu">
          <div className="panneau-cloche-entete"><strong>Notifications</strong>{nonLues > 0 && <button className="lien" onClick={() => void api.notificationsToutesLues().then(charger)}>Tout marquer comme lu</button>}</div>
          {(donnees?.notifications.length ?? 0) === 0 && <p className="aide">Aucune notification.</p>}
          <ul>
            {donnees?.notifications.map((n) => (
              <li key={n.id} className={n.dateLecture ? 'lue' : 'non-lue'}>
                <button onClick={() => ouvrir(n)}>
                  <span className="notif-titre">{n.titre}</span>
                  <span className="notif-corps">{n.corps}</span>
                  <span className="notif-date">{new Date(n.dateCreation).toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' })}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Menu du nom (v1) : Mon profil › Modifier mon mot de passe ; Mes préférences › Ecran de démarrage / Vue des calendriers par défaut ; Administration › Se connecter en tant que… ; Déconnexion. */
function MenuNom({ profil, admin, surDeconnexion, surPreferences }: { profil: Profil; admin: boolean; surDeconnexion: () => void; surPreferences: () => void }): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const [sous, setSous] = useState<'profil' | 'preferences' | 'admin' | null>(null);
  const [sousSous, setSousSous] = useState<'ecran' | 'vue' | 'theme' | null>(null);
  // Lot 8 : thèmes disponibles (opérateurs actifs) pour le sous-menu « Thème ».
  const [themes, setThemes] = useState<{ code: string; nom: string }[]>([]);
  useEffect(() => { if (ouvert && themes.length === 0) void api.operateursPublics().then((l) => setThemes(l.map((o) => ({ code: o.code, nom: o.nom })))).catch(() => undefined); }, [ouvert, themes.length]);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const f = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOuvert(false); setSous(null); setSousSous(null); } };
    document.addEventListener('mousedown', f); return () => document.removeEventListener('mousedown', f);
  }, []);
  const pref = profil.preferences;
  function fermer(): void { setOuvert(false); setSous(null); setSousSous(null); }
  function poser(dto: Parameters<typeof api.preferences>[0]): void {
    if (dto.theme !== undefined) { try { window.sessionStorage.removeItem('playtime.theme.session'); } catch { /* ignoré */ } } // la préférence reprend la main
    void api.preferences(dto).then(surPreferences); fermer();
  }
  return (
    <div className="menu-nom" ref={ref}>
      <button className="nom-utilisateur" aria-haspopup="menu" aria-expanded={ouvert} onClick={() => { setOuvert(!ouvert); setSous(null); }}>{profil.prenom} {profil.nom}<span className="chevron">▾</span></button>
      {ouvert && (
        <ul className="menu-deroulant" role="menu">
          <li onMouseEnter={() => { setSous('profil'); setSousSous(null); }}><button role="menuitem" onClick={() => setSous('profil')}>Mon profil <span>›</span></button>
            {sous === 'profil' && <ul className="sous-menu"><li><button onClick={() => { naviguer('profil', { onglet: 'mot-de-passe' }); fermer(); }}>Modifier mon mot de passe</button></li><li><button onClick={() => { naviguer('profil'); fermer(); }}>Mon profil (contrats, historiques, versions)</button></li></ul>}
          </li>
          <li onMouseEnter={() => setSous('preferences')}><button role="menuitem" onClick={() => setSous('preferences')}>Mes préférences <span>›</span></button>
            {sous === 'preferences' && (
              <ul className="sous-menu">
                <li onMouseEnter={() => setSousSous('ecran')}><button onClick={() => setSousSous('ecran')}>Ecran de démarrage <span>›</span></button>
                  {sousSous === 'ecran' && <ul className="sous-menu">{ECRANS.map(([v, l]) => <li key={v}><button onClick={() => poser({ menuDemarrage: v })}>{pref.menuDemarrage === v ? '✓ ' : ''}{l}</button></li>)}</ul>}
                </li>
                <li onMouseEnter={() => setSousSous('theme')}><button onClick={() => setSousSous('theme')}>Thème <span>›</span></button>
                  {sousSous === 'theme' && <ul className="sous-menu sous-menu-long"><li><button onClick={() => poser({ theme: null })}>{!pref.theme ? '✓ ' : ''}Thème de mon opérateur ({profil.operateur.nom})</button></li>{themes.map((t) => <li key={t.code}><button onClick={() => poser({ theme: t.code })}>{pref.theme === t.code ? '✓ ' : ''}{t.nom}</button></li>)}</ul>}
                </li>
                <li onMouseEnter={() => setSousSous('vue')}><button onClick={() => setSousSous('vue')}>Vue des calendriers par défaut <span>›</span></button>
                  {sousSous === 'vue' && <ul className="sous-menu">{VUES.map(([vue, fen, l]) => <li key={l}><button onClick={() => poser({ vueCalendrier: vue, fenetreHoraire: fen })}>{pref.vueCalendrier === vue && pref.fenetreHoraire === fen ? '✓ ' : ''}{l}</button></li>)}</ul>}
                </li>
              </ul>
            )}
          </li>
          {admin && (
            <li onMouseEnter={() => { setSous('admin'); setSousSous(null); }}><button role="menuitem" onClick={() => setSous('admin')}>Administration <span>›</span></button>
              {sous === 'admin' && <ul className="sous-menu"><li><button onClick={() => { naviguer('parametres-techniques', { outil: 'godview' }); fermer(); }}>Se connecter en tant que…</button></li></ul>}
            </li>
          )}
          <li><button role="menuitem" onClick={surDeconnexion}>Déconnexion</button></li>
        </ul>
      )}
    </div>
  );
}

/** Onglets « Outils » (inertes en v1) — accueillent les fonctions v2 correspondantes. */
function Outils({ section, admin }: { section: string; admin: boolean }): JSX.Element {
  return (
    <section>
      <div className="cadre-outils">
        {section === 'planning-outils' && <>
          <button className="large" disabled={!admin} onClick={() => naviguer('parametres-techniques', { outil: 'import-filemaker' })}>Importer les plannings depuis un fichier Excel (FileMaker)</button>
          <p className="aide">Réservé à l'administrateur — remplace l'alimentation quotidienne FileMaker de la v1 (lot F).</p>
        </>}
        {section === 'prestations-outils' && <p className="aide">Aucun outil disponible pour les prestations.</p>}
        {section === 'compteurs-outils' && <>
          <button className="large" disabled={!admin} onClick={() => naviguer('parametres-techniques', { outil: 'batch' })}>Recalculer les compteurs</button>
          <button className="large" disabled={!admin} onClick={() => naviguer('parametres-techniques', { outil: 'export-rh' })}>Export Excel RH</button>
        </>}
      </div>
    </section>
  );
}
